import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, Mail, Settings2 } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import { fmtDT } from "@/lib/timefmt";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState({ personal_email: "", work_email: "", recovery_email: "" });
  const [savingEmails, setSavingEmails] = useState(false);
  const [prefs, setPrefs] = useState({ theme: "system", email_my_reports: false, email_include_changed: false, weekly_digest_day: null, location_id: "", hide_menu: false, report_email_daily: "signin", report_email_weekly: "signin", report_email_digest: "signin", auth_nudge_enabled: false, auth_nudge_days: 0, auth_nudge_hours: 4 });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    setEmails({
      personal_email: user?.personal_email || "",
      work_email: user?.work_email || "",
      recovery_email: user?.recovery_email || "",
    });
  }, [user]);
  useEffect(() => {
    if (!user) return;
    setPrefs({
      theme: user.theme || "system",
      email_my_reports: !!user.email_my_reports,
      email_include_changed: !!user.email_include_changed,
      weekly_digest_day: user.weekly_digest_day ?? null,
      report_email_daily: user.report_email_daily || "signin",
      report_email_weekly: user.report_email_weekly || "signin",
      report_email_digest: user.report_email_digest || "signin",
      auth_nudge_enabled: !!user.auth_nudge?.enabled,
      auth_nudge_days: user.auth_nudge?.days ?? 0,
      auth_nudge_hours: user.auth_nudge?.hours ?? 4,
      location_id: user.location_id || "",
      hide_menu: !!user.hide_menu,
    });
  }, [user]);

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/locations?company_id=${user.company_id}`).then((r) => setLocations(r.data)).catch(() => {});
  }, [user]);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put("/my/preferences", { ...prefs, location_id: prefs.location_id || null, hide_menu: !!prefs.hide_menu });
      await refresh();
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSavingPrefs(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveEmails = async () => {
    setSavingEmails(true);
    try {
      await api.put("/auth/emails", {
        personal_email: emails.personal_email || null,
        work_email: emails.work_email || null,
        recovery_email: emails.recovery_email || null,
      });
      await refresh();
      toast.success("Email addresses saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSavingEmails(false); }
  };

  return (
    <div className="max-w-md space-y-5">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Profile & Security</h1>
        <p className="text-slate-400 text-sm mt-1">Change your password and set a recovery email.</p>
      </div>
      <form onSubmit={submit} className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="change-password-form">
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Current password</label>
        <PasswordInput testId="current-password" required value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">New password</label>
        <PasswordInput testId="new-password" required minLength={6} value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">Confirm new password</label>
        <PasswordInput testId="confirm-password" required minLength={6} value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <button type="submit" disabled={saving} data-testid="change-password-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          <KeyRound className="h-4 w-4" /> {saving ? "Saving…" : "Change password"}
        </button>
        <p className="mt-3 text-[11px] text-slate-500 font-mono-num" data-testid="password-last-changed">
          Password last changed: {fmtDT(user?.password_changed_at || user?.created_at)}
        </p>
      </form>
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="emails-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-4 w-4 text-slate-400" />
          <div className="font-heading text-lg font-semibold">Email addresses</div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Sign-in email: <span className="font-mono-num text-slate-300">{user?.email}</span> (fixed).
          Work and personal addresses are offered when emailing exports; password reset links go to your sign-in and recovery addresses.
        </p>
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Work email</label>
        <input type="email" value={emails.work_email} onChange={(e) => setEmails({ ...emails, work_email: e.target.value })}
          data-testid="work-email" placeholder="name@unit.mod.uk"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-3 mb-1.5">Personal email</label>
        <input type="email" value={emails.personal_email} onChange={(e) => setEmails({ ...emails, personal_email: e.target.value })}
          data-testid="personal-email" placeholder="you@example.com"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-3 mb-1.5">Recovery email</label>
        <input type="email" value={emails.recovery_email} onChange={(e) => setEmails({ ...emails, recovery_email: e.target.value })}
          data-testid="recovery-email" placeholder="you@example.com"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <button onClick={saveEmails} disabled={savingEmails} data-testid="emails-save"
          className="mt-3 w-full py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm transition-colors disabled:opacity-60">
          {savingEmails ? "Saving…" : "Save email addresses"}
        </button>
      </div>
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="preferences-card">
        <div className="flex items-center gap-2 mb-1">
          <Settings2 className="h-4 w-4 text-slate-400" />
          <div className="font-heading text-lg font-semibold">Preferences</div>
        </div>
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5 mt-3">Theme</label>
        <select value={prefs.theme} onChange={(e) => setPrefs({ ...prefs, theme: e.target.value })} data-testid="pref-theme"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="system">System</option>
          <option value="light">Lite</option>
          <option value="dark">Dark</option>
        </select>
        <label className="flex items-center gap-2 text-sm mt-4">
          <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={prefs.email_my_reports}
            data-testid="pref-email-reports" onChange={(e) => setPrefs({ ...prefs, email_my_reports: e.target.checked })} />
          Email me my daily report
        </label>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={prefs.email_include_changed}
            data-testid="pref-email-changed" onChange={(e) => setPrefs({ ...prefs, email_include_changed: e.target.checked })} />
          Include amended entries in report emails
        </label>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={prefs.hide_menu}
            data-testid="pref-hide-menu" onChange={(e) => setPrefs({ ...prefs, hide_menu: e.target.checked })} />
          Hide the left menu by default (desktop/tablet — a small arrow brings it back)
        </label>
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5 mt-4">Weekly self-digest</label>
        <select value={prefs.weekly_digest_day === null ? "" : String(prefs.weekly_digest_day)}
          onChange={(e) => setPrefs({ ...prefs, weekly_digest_day: e.target.value === "" ? null : Number(e.target.value) })}
          data-testid="pref-digest-day"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="">Off</option>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
        {[["report_email_daily", "Daily report → send to", "pref-report-daily"],
          ["report_email_weekly", "Weekly self-digest → send to", "pref-report-weekly"],
          ...((user?.role === "admin" || user?.admin_level) ? [["report_email_digest", "Company digest → send to", "pref-report-digest"]] : [])].map(([field, label, tid]) => (
          <div key={field}>
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5 mt-3">{label}</label>
            <select value={prefs[field]} onChange={(e) => setPrefs({ ...prefs, [field]: e.target.value })} data-testid={tid}
              className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="signin">Sign-in email ({user?.email})</option>
              <option value="work" disabled={!user?.work_email}>Work email{user?.work_email ? ` (${user.work_email})` : " — not set"}</option>
              <option value="personal" disabled={!user?.personal_email}>Personal email{user?.personal_email ? ` (${user.personal_email})` : " — not set"}</option>
            </select>
          </div>
        ))}
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5 mt-4">Location — drives sunrise/sunset on the timeline</label>
        <select value={prefs.location_id} onChange={(e) => setPrefs({ ...prefs, location_id: e.target.value })} data-testid="pref-location"
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="">Not set</option>
          {locations.filter((l) => l.pers_selectable !== false || l.id === prefs.location_id).map((l) => <option key={l.id} value={l.id}>{l.name}{l.type ? ` — ${l.type}` : ""} ({l.lat}, {l.lng})</option>)}
        </select>
        {user?.auth_officer && (
          <div className="mt-4 pt-4 border-t border-slate-800" data-testid="pref-auth-nudge">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={prefs.auth_nudge_enabled}
                data-testid="pref-auth-nudge-enabled" onChange={(e) => setPrefs({ ...prefs, auth_nudge_enabled: e.target.checked })} />
              Email me when committed tasks in my Auth Officer scope wait for sign-off
            </label>
            {prefs.auth_nudge_enabled && (
              <div className="flex items-center gap-2 mt-2.5 text-sm text-slate-300">
                <span className="text-xs text-slate-500">Nudge me after</span>
                <input type="number" min="0" max="30" value={prefs.auth_nudge_days} data-testid="pref-auth-nudge-days"
                  onChange={(e) => setPrefs({ ...prefs, auth_nudge_days: e.target.value })}
                  className="w-16 px-2 py-1.5 rounded-md bg-slate-950 border border-slate-800 text-sm text-right" />
                <span className="text-xs text-slate-500">days</span>
                <input type="number" min="0" max="23" value={prefs.auth_nudge_hours} data-testid="pref-auth-nudge-hours"
                  onChange={(e) => setPrefs({ ...prefs, auth_nudge_hours: e.target.value })}
                  className="w-16 px-2 py-1.5 rounded-md bg-slate-950 border border-slate-800 text-sm text-right" />
                <span className="text-xs text-slate-500">hours</span>
              </div>
            )}
          </div>
        )}
        <button onClick={savePrefs} disabled={savingPrefs} data-testid="pref-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-60">
          {savingPrefs ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}
